import { useState, useRef, useCallback } from 'react';
import { Mic, MicOff, Square, Loader } from 'lucide-react';
import API from '../api/client';

/**
 * VoiceDictation component
 * Props:
 *   onTranscription(text) - called when transcription is ready
 *   context - optional context string to guide Whisper
 *   disabled - boolean
 */
export default function VoiceDictation({ onTranscription, context = '', disabled = false }) {
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const options = { mimeType: 'audio/webm;codecs=opus' };
      const mr = new MediaRecorder(stream, MediaRecorder.isTypeSupported(options.mimeType) ? options : {});
      chunksRef.current = [];

      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        await sendAudio();
      };

      mr.start(1000);
      mediaRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch (e) {
      setError('Microphone non disponible. Vérifiez les permissions.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== 'inactive') {
      mediaRef.current.stop();
    }
    clearInterval(timerRef.current);
    setRecording(false);
    setLoading(true);
  }, []);

  const sendAudio = async () => {
    const mimeType = mediaRef.current?.mimeType || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type: mimeType });
    if (blob.size < 1000) {
      setLoading(false);
      setError('Enregistrement trop court.');
      return;
    }
    const formData = new FormData();
    const ext = mimeType.includes('ogg') ? '.ogg' : '.webm';
    formData.append('audio', blob, `dictation${ext}`);
    formData.append('language', 'fr');
    formData.append('context', context || 'Consultation médicale, symptômes, examen clinique, diagnostic');
    try {
      const res = await API.post('/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      onTranscription(res.data.text);
    } catch (e) {
      setError('Erreur de transcription. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = s => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  return (
    <div className="flex items-center gap-2">
      {!recording && !loading && (
        <button
          type="button"
          onClick={startRecording}
          disabled={disabled}
          data-testid="voice-dictation-start"
          className="flex items-center gap-1.5 px-2.5 py-1.5 border border-stone-300 bg-white hover:bg-red-50 hover:border-red-300 disabled:opacity-40 text-stone-500 hover:text-red-600 rounded-md text-xs font-medium transition-all"
          title="Dicter par voix"
        >
          <Mic size={14} />
          Dicter
        </button>
      )}
      {recording && (
        <button
          type="button"
          onClick={stopRecording}
          data-testid="voice-dictation-stop"
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-medium animate-pulse"
        >
          <Square size={12} />
          <span className="font-mono">{fmt(seconds)}</span>
          Arrêter
        </button>
      )}
      {loading && (
        <span className="flex items-center gap-1.5 text-xs text-stone-500 px-2.5 py-1.5 bg-stone-50 rounded-md">
          <Loader size={12} className="animate-spin text-emerald-600" />
          Transcription...
        </span>
      )}
      {error && (
        <span className="text-xs text-red-600">{error}</span>
      )}
    </div>
  );
}
